import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Loader2, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal shown when a guest user tries to save a journal entry.
 * Handles email signup + verification code flow, then upgrades the guest account.
 * On success, calls onUpgraded(updatedUser) so the parent can continue saving.
 */
function GuestSignupModal({ onClose, onUpgraded, userName }) {
  const [step, setStep] = useState('signup'); // 'signup' | 'verify'
  const [name, setName] = useState(userName || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !email.includes('@')) { setError('Enter a valid email'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/guest/upgrade`, { email: email.trim(), password, name: name.trim() });
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length < 4) { setError('Enter the code from your email'); return; }

    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/guest/verify`, { email: email.trim(), code });
      if (res.data.user) {
        onUpgraded(res.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      await axios.post(`${API}/auth/guest/upgrade`, { email: email.trim(), password, name: name.trim() });
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend');
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-card-solid w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative px-6 pt-6 pb-2">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-secondary/50"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                <Mail className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-xl font-serif font-medium text-foreground text-center">
                {step === 'signup' ? 'Create your account' : 'Check your email'}
              </h2>
              <p className="text-sm text-muted-foreground text-center mt-1.5 leading-relaxed">
                {step === 'signup'
                  ? 'Sign up to save your entry. Your story will be saved right after.'
                  : `We sent a code to ${email}`}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="px-6 pb-6 pt-4">
            {step === 'signup' ? (
              <form onSubmit={handleSignup} className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-secondary/50 border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    autoFocus
                    className="w-full bg-secondary/50 border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password (6+ chars)"
                    className="w-full bg-secondary/50 border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                {error && <p className="text-xs text-red-500 px-1">{error}</p>}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary text-white rounded-xl py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {isLoading ? 'Sending code...' : 'Continue'}
                </button>
                <p className="text-xs text-muted-foreground text-center">
                  By signing up you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms & Privacy Policy</a>
                </p>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="space-y-3">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  autoFocus
                  inputMode="numeric"
                  className="w-full bg-secondary/50 border border-border rounded-xl px-4 py-3 text-center text-lg font-mono tracking-[0.5em] focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                {error && <p className="text-xs text-red-500 px-1">{error}</p>}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary text-white rounded-xl py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {isLoading ? 'Verifying...' : 'Verify & Save'}
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  className="w-full text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                >
                  Didn't get it? Resend code
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('signup'); setCode(''); setError(''); }}
                  className="w-full text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                >
                  Use a different email
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default GuestSignupModal;
